/**
 * The continuous sounds (04 §5), from state rather than events: the tractor beam's two-tone
 * warble while it is out and the Empress's lance burning. Each is a few drones described as data;
 * ambience.ts builds them and lets them go. Pure.
 */

import type { Ambience } from '../types';
import type { FilterSpec, Wave } from '../voice';

/** A sound with no end of its own: one oscillator (or the noise), wobbled, through a filter. */
export interface DroneSpec {
  readonly wave: Wave;
  /** Hz; for noise, the generator's rate. */
  readonly freq: number;
  /** Pulse width (pulse only). */
  readonly width?: number;
  /** The pitch jumps between `freq` and `cents` above it, `rate` times a second: two tones in turn. */
  readonly warble?: { readonly rate: number; readonly cents: number };
  /** A slow sine drift of the whole pitch, +-`cents`. */
  readonly drift?: { readonly rate: number; readonly cents: number };
  readonly filter?: Pick<FilterSpec, 'type' | 'freq' | 'q'>;
  /** Level 0..1 before the bus. */
  readonly level: number;
  /** Seconds to fade in. */
  readonly attack: number;
}

/** The beam's lower tone (C5), the gap to its upper one (a major third), and tone pairs a second. */
export const BEAM_HZ = 523.25;
export const BEAM_CENTS = 400;
export const BEAM_RATE = 12;

const BEAM_WARBLE = { rate: BEAM_RATE, cents: BEAM_CENTS } as const;
/** The whole warble leans up and down a quarter tone, slowly: it should feel like it is pulling. */
const BEAM_DRIFT = { rate: 0.7, cents: 50 } as const;

export const DRONES: Readonly<Record<keyof Ambience, readonly DroneSpec[]>> = {
  // A soft triangle carries it; a thin pulse an octave below gives the warble an edge.
  beam: [
    { wave: 'triangle', freq: BEAM_HZ, warble: BEAM_WARBLE, drift: BEAM_DRIFT, level: 0.13, attack: 0.06 },
    {
      wave: 'pulse', width: 0.25, freq: BEAM_HZ / 2, warble: BEAM_WARBLE, drift: BEAM_DRIFT,
      filter: { type: 'lowpass', freq: 2400, q: 0 }, level: 0.035, attack: 0.06,
    },
  ],
  // Harsh on purpose: a low sawtooth jumping a fourth 43 times a second, under hissing noise.
  lance: [
    {
      wave: 'sawtooth', freq: 87, warble: { rate: 43, cents: 500 },
      filter: { type: 'lowpass', freq: 3200, q: 4 }, level: 0.2, attack: 0.02,
    },
    {
      wave: 'noise', freq: 11000, warble: { rate: 29, cents: 1200 },
      filter: { type: 'highpass', freq: 1800, q: 0 }, level: 0.14, attack: 0.02,
    },
  ],
};

const okHz = (hz: number): boolean => Number.isFinite(hz) && hz >= 10 && hz <= 24000;
const okWobble = (w: { rate: number; cents: number } | undefined): boolean =>
  w === undefined || (w.rate > 0 && w.rate <= 100 && w.cents > 0 && w.cents <= 2400);

/** Why a drone cannot be played, or null if it is sound. */
export function checkDrone(d: DroneSpec): string | null {
  if (!okHz(d.freq)) return `freq ${d.freq}`;
  if (!(d.level > 0 && d.level <= 1)) return `level ${d.level}`;
  if (!(d.attack > 0 && d.attack <= 2)) return `attack ${d.attack}`;
  if (d.width !== undefined && !(d.width > 0 && d.width < 1)) return `width ${d.width}`;
  if (!okWobble(d.warble)) return 'warble';
  if (!okWobble(d.drift)) return 'drift';
  if (d.filter && (!okHz(d.filter.freq) || !Number.isFinite(d.filter.q))) return 'filter';
  return null;
}
